<template>
  <div id="category-dashboard" class="container page">
    <div class="row">
      <div class="col-xs-12 col-md-10 col-md-offset-1 col-lg-8 col-lg-offset-2">
        <back-button :back-route="{name: 'categoryChooser', params: {area: currentAreaId}}"></back-button>
        <div class="row category-details">
          <div class="col-xs-12">
            <div class="category-image">
              <img :src="category.title_image_path" class="img img-responsive" />
            </div>

            <h4>{{ $t("CATEGORY_DASHBOARD_COURSE") }}</h4>
            <h1>{{ category.name }}</h1>

            <p class="category-description">{{ category.description }}</p>

            <div v-if="category.chapters" class="category-details-progress">
              <progress-bar
                :percent="(completeChapters / category.chapters.length) * 100"
                :height="12"
              ></progress-bar>
              <div class="progress-text">
                {{ completeChapters }}<slash style="width: 6px; height: 10px; margin: 0 2px" /><span class="all-count">{{ category.chapters.length }}</span>
              </div>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-xs-12 chapters-heading">
            <h3>{{ $t("CATEGORY_DASHBOARD_CHAPTERS") }}</h3>
          </div>

          <div
            v-for="chapter in category.chapters || []"
            :key="chapter.id"
            class="col-xs-12"
          >
            <div class="card chapter-card">
              <div class="title-container">
                <h2>
                  <article-icon
                    v-if="chapter.type === 'article'"
                    class="svg-icon medium"
                  />
                  <video-icon
                    v-else-if="chapter.type === 'video'"
                    class="svg-icon medium"
                  />
                  <trivia-icon
                    v-else-if="chapter.type === 'trivia'"
                    class="svg-icon medium"
                  />
                  {{ chapter.name }}
                </h2>
                <p v-if="chapter.description" class="chapter-description">{{ chapter.description }}</p>
              </div>

              <div class="chapter-progress-button">
                <div style="position: relative">
                  <progress-bar
                    :percent="chapterProgressPercent(chapter)"
                  ></progress-bar>
                  <check
                    v-if="chapterProgressPercent(chapter) === 100"
                    class="svg-icon"
                    style="
                      position: absolute;
                      right: -14px;
                      top: -14px;
                      z-index: 1;
                    "
                  />
                </div>
                <button
                  class="btn primary-btn"
                  @click="goToChapter(chapter.id)"
                >
                  {{ chapterProgressPercent(chapter) === 0 ? $t("BEGIN") : chapterProgressPercent(chapter) === 100 ? $t("VIEW_AGAIN") : $t("CONTINUE") }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<!---------------------------------------------------------------------------->
<script>
import { mapGetters } from "vuex";
import log from "src/services/logger";
import notificationService from "src/services/notificationService";
import BackButton from "Components/common/BackButton";
import ProgressBar from "Components/common/ProgressBar";
import Check from "Components/Svg/Check";
import ArticleIcon from "Components/Svg/ArticleIcon";
import VideoIcon from "Components/Svg/VideoIcon";
import TriviaIcon from "Components/Svg/TriviaIcon";
import Slash from "Components/Svg/Slash";
import filter from "lodash/filter";

const logTag = "CategoryDashboard";

export default {
  components: {
    BackButton,
    ProgressBar,
    Check,
    ArticleIcon,
    VideoIcon,
    TriviaIcon,
    Slash,
  },

  created: function () {
    const sameCategory = String(this.category.id) === this.$route.params.category;
    if (!sameCategory) {
      this.$store.dispatch("emptyCategory");
    }

    let wasAllChaptersComplete, wasAreaComplete;
    const chapters = this.category.chapters || [];
    wasAllChaptersComplete = sameCategory && chapters.length > 0 && chapters.every(c => c.complete);
    wasAreaComplete = this.isAreaComplete();

    Promise.all([
      this.initCategory(),
      this.$store.dispatch("downloadUserData"),
    ]).then(() => {
      if (sameCategory) {
        this.checkCompletionModals(wasAllChaptersComplete, wasAreaComplete);
      }
    });
  },

  watch: {
    "$route.params.category": function () {
      this.$store.dispatch("emptyCategory");
      this.initCategory();
    },
  },

  data() {
    return {};
  },

  computed: {
    ...mapGetters(["category", "currentAreaId", "currentCategories", "categoriesProgress", "currentAreaCssClass"]),
    completeChapters() {
      return filter(this.category.chapters || [], { complete: true }).length;
    },
  },

  methods: {
    initCategory() {
      const categoryId = this.$route.params.category;
      return this.$store
        .dispatch("downloadCategory", categoryId)
        .catch((reason) => {
          log.log(logTag, "Download category error, reason follows:");
          log.log(logTag, reason);
        });
    },

    goToChapter(chapterId) {
      this.$router.push(`/categories/${this.category.id}/chapter/${chapterId}`);
    },

    isAreaComplete() {
      const categories = this.currentCategories || [];
      if (!categories.length) return false;
      return categories.every(cat => {
        const p = this.categoriesProgress[cat.id];
        return p && p.total_chapters > 0 && p.complete_chapters === p.total_chapters;
      });
    },

    checkCompletionModals(wasAllChaptersComplete, wasAreaComplete) {
      const chapters = this.category.chapters || [];
      const isNowAllChaptersComplete = chapters.length > 0 && chapters.every(c => c.complete);
      const isNowAreaComplete = this.isAreaComplete();

      if (!wasAreaComplete && isNowAreaComplete) {
        notificationService.areaCompleted(this.currentAreaCssClass);
      } else if (!wasAllChaptersComplete && isNowAllChaptersComplete) {
        notificationService.courseCompleted(this.currentAreaCssClass);
      }
    },

    chapterProgressPercent(chapter) {
      if (chapter.type === "trivia") {
        const total = chapter.triviaQuestions.length;
        const complete = filter(
          chapter.triviaQuestions,
          (q) => q.state.answered
        ).length;
        return (complete / total) * 100;
      } else {
        return chapter.complete ? 100 : 0;
      }
    },
  },
};
</script>
<!---------------------------------------------------------------------------->
<style lang="scss" type="text/scss">
#category-dashboard .category-description {
  white-space: pre-wrap;
}
</style>
